import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
    Trash2,
    Minus,
    Plus,
    ShoppingCart,
    ShieldCheck,
    ArrowRight,
    CreditCard,
    Landmark,
    Smartphone,
    Crown,
    Wallet,
    Gift,
    Truck,
    Zap,
    XCircle,
    ShoppingBag,
} from "lucide-react";
import { MolecularBg } from "../components/MolecularBg";
import { useCart, effectivePrice, isOnSale } from "../context/CartContext";
import { DispatchCountdown } from "../components/DispatchCountdown";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import { toast } from "sonner";
import { trackCheckoutStart } from "../lib/tracker";
import { ProductImage } from "../components/ProductImage";

// All built-in Stripe-powered checkout rails (Card, BNPL, Apple/Google Pay)
// are DISABLED site-wide per operator directive 2026-02-20. The only
// customer-selectable methods are now the admin-managed manual ones
// (Bank Transfer + PayID). Keep the array in place so flipping the flag
// back to true in future doesn't require re-adding routes/tests.
const BUILTIN_METHODS = [];
// Previous built-ins kept as reference only:
// { id: "stripe", label: "Card", icon: CreditCard, sub: "Visa · Mastercard · Apple Pay · Google Pay" },
// { id: "bnpl", label: "Buy Now Pay Later", icon: ShoppingBag, sub: "Pay in instalments · Afterpay · Klarna · Zip (when eligible)" },

// Map admin-configured `icon` slug → lucide component.
const MANUAL_METHOD_ICONS = {
    smartphone: Smartphone,
    landmark: Landmark,
    wallet: Wallet,
    card: CreditCard,
    bag: ShoppingBag,
};

export const CartPage = () => {
    const { items, updateQuantity, removeItem, subtotalRrp, subtotalSale, savingsFromSale, promoCode, setPromoCode, clear: clearCart } = useCart();
    const { user } = useAuth();
    const [email, setEmail] = useState("");
    const [name, setName] = useState("");
    const [codeInput, setCodeInput] = useState(promoCode || "");
    const [validation, setValidation] = useState(null);
    const [validationError, setValidationError] = useState(null);
    // Default to no method selected — gets auto-set to the first manual
    // method (Bank Transfer / PayID) once the admin-managed list loads.
    const [method, setMethod] = useState("");
    const [busy, setBusy] = useState(false);
    const [applyStoreCredit, setApplyStoreCredit] = useState(false);
    const [preview, setPreview] = useState(null); // VIP + credit pricing from /checkout/preview
    const [voucherInput, setVoucherInput] = useState("");
    const [voucherCode, setVoucherCode] = useState(null);
    const [voucherLookup, setVoucherLookup] = useState(null);
    const [voucherError, setVoucherError] = useState(null);
    // Mixed pre-order + in-stock choice. Default to "ship_together" for safety.
    const [mixedOrderChoice, setMixedOrderChoice] = useState("ship_together");
    const preOrderItems = items.filter(i => i.pre_order?.enabled);
    const inStockItems = items.filter(i => !i.pre_order?.enabled);
    const isMixedOrder = preOrderItems.length > 0 && inStockItems.length > 0;
    const hasAnyPreOrder = preOrderItems.length > 0;
    const [shipping, setShipping] = useState({
        full_name: "",
        line1: "",
        line2: "",
        suburb: "",
        state: "",
        postcode: "",
        country: "Australia",
        phone: "",
    });
    const [shippingMethods, setShippingMethods] = useState([]);
    const [shippingMethodCode, setShippingMethodCode] = useState(null);
    const navigate = useNavigate();

    // Fetch admin-managed manual payment methods (enabled + visible at checkout).
    const [manualMethods, setManualMethods] = useState([]);
    useEffect(() => {
        let cancelled = false;
        api.get("/payment-methods/public")
            .then((r) => {
                if (cancelled) return;
                const list = Array.isArray(r.data) ? r.data : [];
                setManualMethods(list);
                // Auto-select the first visible method so the user doesn't
                // have to click before the checkout button appears.
                setMethod((prev) => prev || list[0]?.slug || "");
            })
            .catch(() => { if (!cancelled) setManualMethods([]); });
        return () => { cancelled = true; };
    }, []);

    // Temble gateway visibility — only shown when the backend reports
    // enabled=true. Stripe stays the default; Temble is additive.
    const [tembleConfig, setTembleConfig] = useState({ enabled: false, mode: "test" });
    useEffect(() => {
        let cancelled = false;
        api.get("/checkout/temble/config")
            .then((r) => {
                if (!cancelled && r.data && typeof r.data === "object") {
                    setTembleConfig({
                        enabled: !!r.data.enabled,
                        mode: r.data.mode || "test",
                    });
                }
            })
            .catch(() => {});
        return () => { cancelled = true; };
    }, []);

    // Composed list of everything the customer can choose at checkout.
    const METHODS = React.useMemo(() => {
        const builtin = [...BUILTIN_METHODS];
        if (tembleConfig.enabled) {
            builtin.push({
                id: "temble",
                label: tembleConfig.mode === "test" ? "Pay with Temble (Test)" : "Pay with Temble",
                icon: Zap,
                sub: "Secure hosted checkout · instant confirmation",
            });
        }
        const manual = manualMethods.map((m) => ({
            id: m.slug,
            label: m.name,
            icon: MANUAL_METHOD_ICONS[(m.icon || "").toLowerCase()] || Wallet,
            sub: m.short_description || "",
            manual: true,
        }));
        return [...builtin, ...manual];
    }, [manualMethods, tembleConfig]);

    // If the customer had a method selected that later got disabled or
    // removed from the available list, auto-switch to the first available
    // one so the checkout CTA stays visible.
    useEffect(() => {
        const ids = METHODS.map((m) => m.id);
        if (ids.length && !ids.includes(method)) setMethod(ids[0]);
    }, [METHODS, method]);

    const updateShipping = (k, v) => setShipping((s) => ({ ...s, [k]: v }));
    const shippingValid = () =>
        shipping.full_name.trim() && shipping.line1.trim() && shipping.suburb.trim() &&
        shipping.state.trim() && shipping.postcode.trim() && shipping.country.trim();

    // Auto-fill from logged-in user
    useEffect(() => {
        if (user && !email) setEmail(user.email);
        if (user && !name && user.name) setName(user.name);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user]);

    // Fetch combined VIP + credit pricing preview whenever cart / code / credit toggle changes.
    useEffect(() => {
        if (items.length === 0) { setPreview(null); return; }
        let cancelled = false;
        api.post("/checkout/preview", {
            items: items.map((i) => ({ product_slug: i.slug, quantity: i.quantity })),
            origin_url: typeof window !== "undefined" ? window.location.origin : "",
            customer_email: email || undefined,
            promo_code: validation?.code_applied || undefined,
            voucher_code: voucherCode || undefined,
            apply_store_credit: applyStoreCredit,
            shipping_method_code: shippingMethodCode || undefined,
            mixed_order_choice: isMixedOrder ? mixedOrderChoice : undefined,
        })
            .then((r) => { if (!cancelled) setPreview(r.data); })
            .catch(() => { if (!cancelled) setPreview(null); });
        return () => { cancelled = true; };
    }, [items, email, validation?.code_applied, applyStoreCredit, voucherCode, user, shippingMethodCode]);

    // Fetch available shipping methods + lock in the default selection.
    useEffect(() => {
        let cancelled = false;
        const sub = preview?.subtotal_before_shipping ?? subtotalSale;
        api.get(`/shipping-methods`, { params: { subtotal: sub } })
            .then((r) => {
                if (cancelled) return;
                const list = Array.isArray(r.data) ? r.data : [];
                setShippingMethods(list);
                if (!shippingMethodCode && list.length) {
                    const def = list.find((m) => m.is_default) || list[0];
                    setShippingMethodCode(def.code);
                }
            })
            .catch(() => { if (!cancelled) setShippingMethods([]); });
        return () => { cancelled = true; };
    }, [preview?.subtotal_before_shipping, subtotalSale, shippingMethodCode]);

    // Re-validate the code whenever the cart changes OR the saved code changes.
    useEffect(() => {
        if (!promoCode || items.length === 0) {
            setValidation(null);
            setValidationError(null);
            return;
        }
        let cancelled = false;
        api.post("/discounts/validate", {
            code: promoCode,
            items: items.map((i) => ({ product_slug: i.slug, quantity: i.quantity })),
            customer_email: email || undefined,
        })
            .then((r) => {
                if (cancelled) return;
                setValidation(r.data);
                setValidationError(null);
                // If the code yielded no benefit, drop it silently so totals don't show a stale "Applied"
                if (!r.data.code_applied && !r.data.free_shipping) {
                    // Keep input but show message
                }
            })
            .catch((err) => {
                if (cancelled) return;
                setValidation(null);
                setValidationError(err?.response?.data?.detail || "Code error");
            });
        return () => { cancelled = true; };
    }, [promoCode, items, email]);

    const applyCode = () => {
        const c = codeInput.trim().toUpperCase();
        if (!c) {
            setPromoCode(null);
            setValidation(null);
            setValidationError(null);
            return;
        }
        setPromoCode(c);
    };

    const removeCode = () => {
        setCodeInput("");
        setPromoCode(null);
        setValidation(null);
        setValidationError(null);
    };

    const codeDiscount = validation?.code_applied ? (subtotalSale - validation.subtotal_final) : 0;
    // Pricing preview values from server. VIP now STACKS on sale, so we render
    // both the sale-savings line and the VIP-discount line separately.
    const vipApplied = !!preview?.vip_applied;
    // Prefer the explicit `vip_discount_amount` field the backend emits so the
    // number shown in the summary equals the actual discount applied (VIP % of
    // the SALE-DISCOUNTED subtotal, not RRP).
    const vipDiscount = vipApplied
        ? Number(
              preview.vip_discount_amount ??
                  ((preview.sale_only_subtotal ?? preview.subtotal_rrp) - preview.subtotal_after_discounts)
          )
        : 0;
    const creditApplied = Number(preview?.store_credit_applied || 0);
    const creditBalance = Number(preview?.store_credit_balance || user?.store_credit_balance || 0);
    const voucherApplied = Number(preview?.voucher_applied || 0);
    const voucherActiveCode = preview?.voucher_code || (voucherApplied > 0 ? voucherCode : null);
    const shippingCost = Number(preview?.shipping_cost || 0);
    const selectedShippingMethod = preview?.shipping_method || shippingMethods.find((m) => m.code === shippingMethodCode);
    const freeShippingFromCode = !!validation?.free_shipping;
    const effectiveShippingCost = freeShippingFromCode ? 0 : shippingCost;
    const grandTotal = preview
        ? Math.max(0, Number(preview.total) - (freeShippingFromCode ? shippingCost : 0))
        : Math.max(0, subtotalSale - codeDiscount);

    // Out-of-stock detection from the preview (server is authoritative)
    const oosItems = Array.isArray(preview?.out_of_stock_items) ? preview.out_of_stock_items : [];
    const hasOosBlocker = oosItems.length > 0;

    // Free-shipping promo progress (e.g. "Free Express over A$150")
    const promoSubtotal = Number(preview?.subtotal_before_shipping ?? subtotalSale ?? 0);
    const freeShipPromo = (() => {
        const candidate = shippingMethods.find((m) => Number(m.free_above) > 0);
        if (!candidate) return null;
        const threshold = Number(candidate.free_above);
        const remaining = Math.max(0, threshold - promoSubtotal);
        const progressPct = Math.min(100, Math.round((promoSubtotal / threshold) * 100));
        return {
            method_name: candidate.name,
            method_code: candidate.code,
            threshold,
            remaining,
            unlocked: remaining <= 0,
            progressPct,
        };
    })();

    const applyVoucher = async () => {
        const code = (voucherInput || "").trim();
        if (!code) return;
        setVoucherError(null);
        try {
            const { data } = await api.post("/vouchers/lookup", { code });
            if (!data.found || data.status !== "active") {
                setVoucherError(data.status === "expired" ? "This voucher has expired."
                    : data.status === "disabled" ? "This voucher has been disabled."
                    : data.status === "redeemed" ? "This voucher has been fully redeemed."
                    : "We couldn't find that voucher.");
                setVoucherCode(null);
                setVoucherLookup(null);
                return;
            }
            setVoucherCode(data.code);
            setVoucherLookup(data);
            toast.success(`Voucher applied — A$${data.balance.toFixed(2)} available`);
        } catch {
            setVoucherError("Could not validate that voucher right now.");
        }
    };

    const removeVoucher = () => {
        setVoucherCode(null);
        setVoucherLookup(null);
        setVoucherInput("");
        setVoucherError(null);
    };

    const cartPayload = () => ({
        items: items.map((i) => ({ product_slug: i.slug, quantity: i.quantity })),
        promo_code: validation?.code_applied || undefined,
        voucher_code: voucherCode || undefined,
        shipping_method_code: shippingMethodCode || undefined,
            mixed_order_choice: isMixedOrder ? mixedOrderChoice : undefined,
        apply_store_credit: applyStoreCredit && !!user,
    });

    /* ---------- Stripe (hosted checkout) ---------- */
    const stripeCheckout = async () => {
        if (!email) {
            toast.error("Email is required so we can send your order confirmation.");
            return;
        }
        if (!shippingValid()) {
            toast.error("Please fill in your shipping address so we can dispatch your order.");
            return;
        }
        setBusy(true);
        try {
            try { trackCheckoutStart(Number(grandTotal || 0)); } catch { /* tracking is best-effort */ }
            const res = await api.post("/checkout/session", {
                ...cartPayload(),
                origin_url: window.location.origin,
                customer_email: email,
                customer_name: name || shipping.full_name || undefined,
                shipping_address: {
                    ...shipping,
                    line2: shipping.line2 || undefined,
                    phone: shipping.phone || undefined,
                },
            });
            // Fully paid via store credit + voucher → skip Stripe, go straight to order page
            if (res.data.fully_paid) {
                clearCart();
                if (res.data.reference) {
                    navigate(`/order/${res.data.reference}`);
                } else {
                    window.location.href = res.data.url;
                }
                return;
            }
            window.location.href = res.data.url;
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not start Stripe checkout. Try again.");
            setBusy(false);
        }
    };

    /* ---------- Manual (admin-configured methods) ---------- */
    const manualCheckout = async () => {
        if (!email) {
            toast.error("Email is required to create your order.");
            return;
        }
        if (!shippingValid()) {
            toast.error("Please fill in all shipping address fields.");
            return;
        }
        setBusy(true);
        try {
            try { trackCheckoutStart(Number(grandTotal || 0)); } catch { /* tracking is best-effort */ }
            const res = await api.post("/checkout/manual", {
                ...cartPayload(),
                customer_email: email,
                customer_name: name || undefined,
                method,
                shipping_address: {
                    ...shipping,
                    line2: shipping.line2 || undefined,
                    phone: shipping.phone || undefined,
                },
                apply_store_credit: applyStoreCredit && !!user,
            });
            clearCart();
            navigate(`/order/${res.data.reference}`);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Failed to create order.");
            setBusy(false);
        }
    };

    /* ---------- Temble (hosted checkout, additive to Stripe) ---------- */
    const tembleCheckout = async () => {
        if (!email) {
            toast.error("Email is required so we can send your order confirmation.");
            return;
        }
        if (!shippingValid()) {
            toast.error("Please fill in your shipping address so we can dispatch your order.");
            return;
        }
        setBusy(true);
        try {
            try { trackCheckoutStart(Number(grandTotal || 0)); } catch { /* tracking is best-effort */ }
            const res = await api.post("/checkout/temble/session", {
                ...cartPayload(),
                origin_url: window.location.origin,
                customer_email: email,
                customer_name: name || shipping.full_name || undefined,
                shipping_address: {
                    ...shipping,
                    line2: shipping.line2 || undefined,
                    phone: shipping.phone || undefined,
                },
            });
            const payUrl = res.data?.pay_url || res.data?.url;
            if (!payUrl) {
                toast.error("Temble did not return a redirect URL.");
                setBusy(false);
                return;
            }
            // We DO NOT mark the order paid here. The webhook is the only
            // thing that can promote the order to paid. The return_url
            // simply shows the user their current order status.
            window.location.href = payUrl;
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not start Temble checkout. Try again.");
            setBusy(false);
        }
    };

    return (
        <div className="pt-28 pb-20 min-h-screen relative" data-testid="cart-page">
            <MolecularBg intensity="low" />
            <div className="container-x relative z-10">
                <span className="eyebrow">// Checkout</span>
                <h1 className="font-display text-4xl md:text-6xl font-extrabold text-white tracking-tighter mt-2 mb-10">
                    Your Cart
                </h1>

                {items.length === 0 ? (
                    <div className="glass-elevated rounded-3xl p-16 text-center" data-testid="cart-empty-state">
                        <ShoppingCart size={48} className="mx-auto mb-4 text-[var(--text-muted)]" />
                        <h2 className="font-display text-2xl font-bold text-white mb-2">Cart is empty</h2>
                        <p className="text-[var(--text-secondary)] mb-6">
                            Add research peptides to begin checkout.
                        </p>
                        <Link to="/shop" className="btn-solid">
                            Browse Catalog <ArrowRight size={14} />
                        </Link>
                    </div>
                ) : (
                    <div className="grid lg:grid-cols-3 gap-8">
                        <div className="lg:col-span-2 space-y-3">
                            {items.map((item) => {
                                const eff = effectivePrice(item);
                                const onSale = isOnSale(item);
                                return (
                                <div
                                    key={item.slug}
                                    className="glass-elevated rounded-2xl p-3 sm:p-4 relative"
                                    data-testid={`cart-item-${item.slug}`}
                                >
                                    {/* Trash button — absolute top-right on all sizes to save horizontal space */}
                                    <button
                                        onClick={() => removeItem(item.slug)}
                                        className="absolute top-3 right-3 p-2 text-[var(--text-muted)] hover:text-red-400 z-10"
                                        data-testid={`cart-remove-${item.slug}`}
                                        aria-label="Remove"
                                    >
                                        <Trash2 size={16} />
                                    </button>
                                    <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4 pr-8 sm:pr-10">
                                        {/* Row 1 on mobile: image + name */}
                                        <div className="flex items-start gap-3 sm:contents min-w-0">
                                            <div className="relative shrink-0 w-24 sm:w-28">
                                                <ProductImage
                                                    product={{ image: item.image, name: item.name, slug: item.slug }}
                                                    ratio="3 / 2"
                                                    className="rounded-xl"
                                                />
                                                {onSale && (
                                                    <span className="absolute -top-1.5 -left-1.5 px-1.5 py-0.5 rounded-full text-[9px] font-mono font-bold uppercase bg-[var(--gold)] text-black z-10" data-testid={`cart-sale-badge-${item.slug}`}>
                                                        Sale
                                                    </span>
                                                )}
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <Link
                                                    to={`/product/${item.slug}`}
                                                    className="font-display font-bold text-white hover:text-[var(--neon-cyan)] block break-words leading-tight text-sm sm:text-base"
                                                >
                                                    {item.name}
                                                </Link>
                                                <div className="text-[11px] sm:text-xs font-mono uppercase tracking-wider sm:tracking-widest text-[var(--text-muted)] mt-1 flex items-center gap-1.5 flex-wrap">
                                                    <span>{item.dosage}</span>
                                                    <span>·</span>
                                                    <span className={onSale ? "text-[var(--gold)]" : ""}>A${eff.toFixed(2)}</span>
                                                    {onSale && (
                                                        <span className="line-through text-[var(--text-muted)]">A${item.price.toFixed(2)}</span>
                                                    )}
                                                </div>
                                                {item.pre_order?.enabled && (
                                                    <div
                                                        className="mt-1.5 inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-[10px] font-mono uppercase tracking-widest border border-[var(--gold)]/50 bg-[var(--gold)]/10 text-[var(--gold)]"
                                                        data-testid={`cart-preorder-badge-${item.slug}`}
                                                    >
                                                        Pre-order
                                                        {item.pre_order?.estimated_dispatch_date && (
                                                            <span className="normal-case tracking-normal text-[var(--text-secondary)]">
                                                                — dispatch {item.pre_order.estimated_dispatch_date}
                                                            </span>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                        {/* Row 2 on mobile: qty picker + line total (pushed down under the image) */}
                                        <div className="flex items-center justify-between gap-3 sm:contents pl-[76px] sm:pl-0">
                                            <div className="flex items-center glass rounded-full border border-white/10 shrink-0">
                                                <button
                                                    onClick={() => updateQuantity(item.slug, item.quantity - 1)}
                                                    className="p-2 text-white hover:text-[var(--neon-cyan)]"
                                                    data-testid={`cart-decrement-${item.slug}`}
                                                    aria-label="Decrease"
                                                >
                                                    <Minus size={14} />
                                                </button>
                                                <span className="px-2 font-mono text-white min-w-[1.5rem] text-center text-sm">
                                                    {item.quantity}
                                                </span>
                                                <button
                                                    onClick={() => updateQuantity(item.slug, item.quantity + 1)}
                                                    className="p-2 text-white hover:text-[var(--neon-cyan)]"
                                                    data-testid={`cart-increment-${item.slug}`}
                                                    aria-label="Increase"
                                                >
                                                    <Plus size={14} />
                                                </button>
                                            </div>
                                            <div className="text-right sm:w-24 shrink-0">
                                                <div className="font-display text-base font-bold text-white">
                                                    A${(eff * item.quantity).toFixed(2)}
                                                </div>
                                                {onSale && (
                                                    <div className="text-[10px] font-mono text-[var(--text-muted)] line-through">
                                                        A${(item.price * item.quantity).toFixed(2)}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                );
                            })}
                        </div>

                        <div className="lg:col-span-1">
                            <div
                                className="glass-elevated rounded-2xl p-6 sticky top-28 neon-border-cyan"
                                data-testid="cart-summary"
                            >
                                <h3 className="font-display text-lg font-bold text-white mb-3">Order Summary</h3>
                                <div className="mb-4">
                                    <DispatchCountdown compact />
                                </div>
                                {freeShipPromo && (
                                    <FreeShippingProgress promo={freeShipPromo} />
                                )}
                                <div className="space-y-2 text-sm">
                                    <div className="flex justify-between text-[var(--text-secondary)]">
                                        <span>Subtotal</span>
                                        <span className="text-white font-mono">A${subtotalRrp.toFixed(2)}</span>
                                    </div>
                                    {savingsFromSale > 0 && (
                                        <div className="flex justify-between text-emerald-300" data-testid="cart-sale-savings">
                                            <span>Sale savings</span>
                                            <span className="font-mono">−A${savingsFromSale.toFixed(2)}</span>
                                        </div>
                                    )}
                                    {validation?.code_applied && codeDiscount > 0 && (
                                        <div className="flex justify-between text-[var(--gold)]" data-testid="cart-discount-line">
                                            <span>Code ({validation.code_applied})</span>
                                            <span className="font-mono">−A${codeDiscount.toFixed(2)}</span>
                                        </div>
                                    )}
                                    {vipApplied && vipDiscount > 0 && (
                                        <div className="flex justify-between text-[var(--gold)]" data-testid="cart-vip-line">
                                            <span className="inline-flex items-center gap-1"><Crown size={12} /> VIP {preview.vip_discount_pct}%</span>
                                            <span className="font-mono">−A${vipDiscount.toFixed(2)}</span>
                                        </div>
                                    )}
                                    {voucherApplied > 0 && (
                                        <div className="flex justify-between text-amber-200" data-testid="cart-voucher-line">
                                            <span className="inline-flex items-center gap-1"><Gift size={12} /> Voucher {voucherActiveCode}</span>
                                            <span className="font-mono">−A${voucherApplied.toFixed(2)}</span>
                                        </div>
                                    )}
                                    {creditApplied > 0 && (
                                        <div className="flex justify-between text-emerald-300" data-testid="cart-credit-line">
                                            <span className="inline-flex items-center gap-1"><Wallet size={12} /> Store credit</span>
                                            <span className="font-mono">−A${creditApplied.toFixed(2)}</span>
                                        </div>
                                    )}
                                    {validation?.free_shipping && (
                                        <div className="flex justify-between text-[var(--gold)]" data-testid="cart-free-shipping">
                                            <span>Free shipping ({validation.code_applied})</span>
                                            <span className="font-mono">Applied</span>
                                        </div>
                                    )}
                                    <div className="flex justify-between text-[var(--text-secondary)]" data-testid="cart-shipping-line">
                                        <span>
                                            Shipping
                                            {selectedShippingMethod?.name && (
                                                <span className="text-[var(--text-muted)] ml-1 text-xs">({selectedShippingMethod.name})</span>
                                            )}
                                        </span>
                                        <span className="text-white font-mono">
                                            {effectiveShippingCost > 0
                                                ? `A$${effectiveShippingCost.toFixed(2)}`
                                                : "Free"}
                                        </span>
                                    </div>
                                    <div className="border-t border-white/10 my-3" />
                                    <div className="flex justify-between font-display text-xl font-bold text-white">
                                        <span>Total</span>
                                        <span data-testid="cart-total">A${grandTotal.toFixed(2)}</span>
                                    </div>
                                </div>

                                {/* Discount code input */}
                                <div className="mt-5" data-testid="cart-code-section">
                                    <p className="text-[10px] font-mono uppercase tracking-[0.25em] text-[var(--text-muted)] mb-2">
                                        Discount code
                                    </p>
                                    {validation?.code_applied ? (
                                        <div className="flex items-center justify-between px-4 py-2.5 bg-[rgba(233,188,11,0.06)] border border-[var(--gold)]/40 rounded-full">
                                            <span className="font-mono text-[var(--gold)] text-sm">
                                                {validation.code_applied}
                                            </span>
                                            <button
                                                onClick={removeCode}
                                                className="text-xs text-[var(--text-secondary)] hover:text-white font-mono uppercase tracking-wider"
                                                data-testid="cart-code-remove"
                                            >
                                                Remove
                                            </button>
                                        </div>
                                    ) : (
                                        <div className="flex gap-2">
                                            <input
                                                type="text"
                                                value={codeInput}
                                                onChange={(e) => setCodeInput(e.target.value.toUpperCase())}
                                                onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), applyCode())}
                                                placeholder="Enter code"
                                                className="flex-1 px-4 py-2.5 bg-black/40 border border-white/10 rounded-full text-sm text-white placeholder:text-[var(--text-muted)] focus:outline-none focus:border-[var(--gold)] font-mono uppercase tracking-wider"
                                                data-testid="cart-code-input"
                                            />
                                            <button
                                                onClick={applyCode}
                                                disabled={!codeInput.trim()}
                                                className="px-4 py-2.5 rounded-full border border-[var(--gold)]/40 text-[var(--gold)] hover:bg-[var(--gold)]/10 text-xs font-mono uppercase tracking-widest disabled:opacity-40"
                                                data-testid="cart-code-apply"
                                            >
                                                Apply
                                            </button>
                                        </div>
                                    )}
                                    {validationError && (
                                        <p className="mt-2 text-xs text-red-300 font-mono" data-testid="cart-code-error">{validationError}</p>
                                    )}
                                    {validation?.message && !validation.code_applied && (
                                        <p className="mt-2 text-xs text-amber-300 font-mono" data-testid="cart-code-message">{validation.message}</p>
                                    )}
                                </div>

                                {/* Gift voucher input */}
                                <div className="mt-4" data-testid="cart-voucher-section">
                                    <label className="text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)] mb-1 block flex items-center gap-1.5">
                                        <Gift size={12} /> Gift voucher
                                    </label>
                                    {!voucherCode ? (
                                        <div className="flex gap-2">
                                            <input
                                                value={voucherInput}
                                                onChange={(e) => setVoucherInput(e.target.value.toUpperCase())}
                                                placeholder="GIFT-XXXX-XXXX"
                                                className="flex-1 px-3 py-2 bg-black/40 border border-white/10 rounded-lg text-sm font-mono tracking-widest text-white placeholder:text-[var(--text-muted)] focus:outline-none focus:border-[var(--gold)]"
                                                data-testid="cart-voucher-input"
                                            />
                                            <button
                                                onClick={applyVoucher}
                                                disabled={!voucherInput.trim()}
                                                className="px-3 py-2 rounded-lg text-xs font-mono uppercase tracking-widest border border-[var(--gold)]/40 text-[var(--gold)] hover:bg-[rgba(233,188,11,0.08)] disabled:opacity-40"
                                                data-testid="cart-voucher-apply"
                                            >
                                                Apply
                                            </button>
                                        </div>
                                    ) : (
                                        <div className="flex items-center justify-between p-3 rounded-lg bg-amber-500/5 border border-amber-500/30" data-testid="cart-voucher-applied">
                                            <div className="min-w-0">
                                                <p className="text-sm text-white font-mono tracking-widest font-bold">{voucherCode}</p>
                                                <p className="text-[10px] text-[var(--text-muted)]">
                                                    Balance A${Number(voucherLookup?.balance || 0).toFixed(2)}
                                                </p>
                                            </div>
                                            <button onClick={removeVoucher} className="text-[var(--text-muted)] hover:text-white text-xs font-mono uppercase tracking-widest" data-testid="cart-voucher-remove">
                                                Remove
                                            </button>
                                        </div>
                                    )}
                                    {voucherError && (
                                        <p className="mt-2 text-xs text-red-300 font-mono" data-testid="cart-voucher-error">{voucherError}</p>
                                    )}
                                </div>

                                {/* Store credit toggle (logged-in customers only) */}
                                {user && creditBalance > 0 && (
                                    <div className="mt-5" data-testid="cart-credit-section">
                                        <label className="flex items-start gap-3 p-3 rounded-xl bg-emerald-500/5 border border-emerald-500/20 cursor-pointer hover:border-emerald-500/40 transition-colors">
                                            <input
                                                type="checkbox"
                                                checked={applyStoreCredit}
                                                onChange={(e) => setApplyStoreCredit(e.target.checked)}
                                                className="mt-1 w-4 h-4 accent-emerald-400"
                                                data-testid="apply-store-credit"
                                            />
                                            <div className="flex-1">
                                                <div className="text-sm text-white font-bold inline-flex items-center gap-1.5">
                                                    <Wallet size={14} className="text-emerald-300" /> Use store credit
                                                </div>
                                                <p className="text-xs text-[var(--text-secondary)] mt-0.5">
                                                    Balance: <span className="font-mono text-emerald-300">A${creditBalance.toFixed(2)}</span>
                                                </p>
                                            </div>
                                        </label>
                                    </div>
                                )}

                                {/* VIP banner (logged-in customers with a tier) */}
                                {user && user.vip_discount_pct > 0 && (
                                    <div className="mt-3 p-3 rounded-xl bg-[rgba(233,188,11,0.06)] border border-[var(--gold)]/30 flex items-center gap-3" data-testid="cart-vip-banner">
                                        <Crown size={16} className="text-[var(--gold)] shrink-0" />
                                        <div>
                                            <p className="text-xs text-white font-bold">
                                                {user.vip_tier?.toUpperCase()} VIP · {user.vip_discount_pct}% off
                                            </p>
                                            <p className="text-[10px] text-[var(--text-secondary)] mt-0.5">
                                                {vipApplied
                                                    ? (savingsFromSale > 0
                                                        ? "Stacked on top of your sale price"
                                                        : "Applied automatically")
                                                    : (validation?.code_applied
                                                        ? "Your code is a better deal right now."
                                                        : "Applied automatically.")}
                                            </p>
                                        </div>
                                    </div>
                                )}

                                {/* Customer details */}
                                <div className="mt-5 space-y-3">
                                    <input
                                        type="email"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        placeholder="Email *"
                                        className="w-full px-4 py-2.5 bg-black/40 border border-white/10 rounded-full text-sm text-white placeholder:text-[var(--text-muted)] focus:outline-none focus:border-[var(--neon-cyan)]"
                                        data-testid="cart-email-input"
                                    />
                                    {(method === "stripe" || method === "bnpl" || METHODS.find((m) => m.id === method)?.manual) && (
                                        <input
                                            type="text"
                                            value={name}
                                            onChange={(e) => setName(e.target.value)}
                                            placeholder="Your name (optional)"
                                            className="w-full px-4 py-2.5 bg-black/40 border border-white/10 rounded-full text-sm text-white placeholder:text-[var(--text-muted)] focus:outline-none focus:border-[var(--neon-cyan)]"
                                            data-testid="cart-name-input"
                                        />
                                    )}
                                </div>

                                {/* Shipping address — required for all payment methods */}
                                {(method === "stripe" || METHODS.find((m) => m.id === method)?.manual) && (
                                    <div className="mt-5">
                                        <p className="text-[10px] font-mono uppercase tracking-[0.25em] text-[var(--text-muted)] mb-2">
                                            Shipping address
                                        </p>
                                        <div className="space-y-2">
                                            <input type="text" value={shipping.full_name} onChange={(e) => updateShipping("full_name", e.target.value)} placeholder="Recipient full name *" className="w-full px-4 py-2 bg-black/40 border border-white/10 rounded-xl text-sm text-white placeholder:text-[var(--text-muted)] focus:outline-none focus:border-[var(--gold)]" data-testid="ship-fullname" />
                                            <input type="text" value={shipping.line1} onChange={(e) => updateShipping("line1", e.target.value)} placeholder="Street address *" className="w-full px-4 py-2 bg-black/40 border border-white/10 rounded-xl text-sm text-white placeholder:text-[var(--text-muted)] focus:outline-none focus:border-[var(--gold)]" data-testid="ship-line1" />
                                            <input type="text" value={shipping.line2} onChange={(e) => updateShipping("line2", e.target.value)} placeholder="Apartment / Unit / PO Box (optional)" className="w-full px-4 py-2 bg-black/40 border border-white/10 rounded-xl text-sm text-white placeholder:text-[var(--text-muted)] focus:outline-none focus:border-[var(--gold)]" data-testid="ship-line2" />
                                            <div className="grid grid-cols-2 gap-2">
                                                <input type="text" value={shipping.suburb} onChange={(e) => updateShipping("suburb", e.target.value)} placeholder="Suburb / City *" className="px-4 py-2 bg-black/40 border border-white/10 rounded-xl text-sm text-white placeholder:text-[var(--text-muted)] focus:outline-none focus:border-[var(--gold)]" data-testid="ship-suburb" />
                                                <input type="text" value={shipping.postcode} onChange={(e) => updateShipping("postcode", e.target.value)} placeholder="Postcode *" className="px-4 py-2 bg-black/40 border border-white/10 rounded-xl text-sm text-white placeholder:text-[var(--text-muted)] focus:outline-none focus:border-[var(--gold)]" data-testid="ship-postcode" />
                                            </div>
                                            <div className="grid grid-cols-2 gap-2">
                                                <select value={shipping.state} onChange={(e) => updateShipping("state", e.target.value)} className="px-4 py-2 bg-black/40 border border-white/10 rounded-xl text-sm text-white focus:outline-none focus:border-[var(--gold)]" data-testid="ship-state">
                                                    <option value="" className="bg-black">State *</option>
                                                    {["NSW","VIC","QLD","WA","SA","TAS","ACT","NT"].map((s) => (<option key={s} value={s} className="bg-black">{s}</option>))}
                                                </select>
                                                <input type="text" value={shipping.country} onChange={(e) => updateShipping("country", e.target.value)} placeholder="Country *" className="px-4 py-2 bg-black/40 border border-white/10 rounded-xl text-sm text-white placeholder:text-[var(--text-muted)] focus:outline-none focus:border-[var(--gold)]" data-testid="ship-country" />
                                            </div>
                                            <input type="tel" value={shipping.phone} onChange={(e) => updateShipping("phone", e.target.value)} placeholder="Phone (optional — for delivery issues)" className="w-full px-4 py-2 bg-black/40 border border-white/10 rounded-xl text-sm text-white placeholder:text-[var(--text-muted)] focus:outline-none focus:border-[var(--gold)]" data-testid="ship-phone" />
                                        </div>
                                    </div>
                                )}
                                {METHODS.find((m) => m.id === method)?.manual && METHODS.find((m) => m.id === method)?.sub && (
                                    <p className="mt-3 text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)] text-center">
                                        {METHODS.find((m) => m.id === method)?.sub}
                                    </p>
                                )}


                                {/* Pre-order notice + mixed-order shipping choice */}
                                {hasAnyPreOrder && (
                                    <div className="mt-5 p-3 rounded-xl border border-[var(--gold)]/40 bg-[var(--gold)]/5 text-xs space-y-2" data-testid="pre-order-notice-cart">
                                        <div className="text-[var(--gold)] font-mono uppercase tracking-widest text-[10px] font-bold">Pre-order in your cart</div>
                                        <ul className="text-[var(--text-secondary)] space-y-0.5">
                                            {preOrderItems.map((it) => (
                                                <li key={it.slug}>
                                                    · <span className="text-white">{it.name}</span>
                                                    {it.pre_order?.expected_arrival_date && (
                                                        <span className="text-[var(--text-muted)]"> — arrives {it.pre_order.expected_arrival_date}</span>
                                                    )}
                                                </li>
                                            ))}
                                        </ul>
                                        {isMixedOrder && (
                                            <div className="pt-2 border-t border-white/10 space-y-2" data-testid="mixed-order-choice">
                                                <div className="text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)]">Your cart has in-stock + pre-order items. Choose how to ship:</div>
                                                <label className="flex items-start gap-2 cursor-pointer">
                                                    <input
                                                        type="radio"
                                                        name="mixedChoice"
                                                        value="ship_together"
                                                        checked={mixedOrderChoice === "ship_together"}
                                                        onChange={() => setMixedOrderChoice("ship_together")}
                                                        className="mt-0.5"
                                                        data-testid="mixed-choice-together"
                                                    />
                                                    <span className="text-[var(--text-secondary)] text-[11px]">
                                                        <span className="text-white font-bold">Ship everything together</span> when the pre-order arrives (one shipment, no extra cost)
                                                    </span>
                                                </label>
                                                <label className="flex items-start gap-2 cursor-pointer">
                                                    <input
                                                        type="radio"
                                                        name="mixedChoice"
                                                        value="ship_separately"
                                                        checked={mixedOrderChoice === "ship_separately"}
                                                        onChange={() => setMixedOrderChoice("ship_separately")}
                                                        className="mt-0.5"
                                                        data-testid="mixed-choice-separately"
                                                    />
                                                    <span className="text-[var(--text-secondary)] text-[11px]">
                                                        <span className="text-white font-bold">Ship available items now</span>, pre-order later (second dispatch may incur an additional shipping fee, contacted before dispatch)
                                                    </span>
                                                </label>
                                            </div>
                                        )}
                                    </div>
                                )}

                                {/* Shipping method selector */}
                                {shippingMethods.length > 0 && (
                                    <div className="mt-5" data-testid="cart-shipping-methods">
                                        <p className="text-[10px] font-mono uppercase tracking-[0.25em] text-[var(--text-muted)] mb-2">
                                            Shipping method
                                        </p>
                                        <div className="space-y-2">
                                            {shippingMethods.map((m) => {
                                                const active = m.code === shippingMethodCode;
                                                const cost = Number(m.effective_price || 0);
                                                const isFreeCode = freeShippingFromCode && active;
                                                const Icon = m.code === "express" ? Zap : Truck;
                                                return (
                                                    <button
                                                        key={m.code}
                                                        onClick={() => setShippingMethodCode(m.code)}
                                                        type="button"
                                                        className={`w-full flex items-center gap-3 p-3 rounded-xl border transition-all text-left ${
                                                            active
                                                                ? "border-[var(--gold)] bg-[rgba(233,188,11,0.08)]"
                                                                : "border-white/10 hover:border-white/20 bg-black/20"
                                                        }`}
                                                        data-testid={`shipping-method-${m.code}`}
                                                    >
                                                        <Icon
                                                            size={16}
                                                            className={active ? "text-[var(--gold)]" : "text-[var(--text-muted)]"}
                                                        />
                                                        <div className="flex-1 min-w-0">
                                                            <div className="text-sm text-white font-semibold flex items-center gap-2">
                                                                {m.name}
                                                                {(cost === 0 || m.is_free_promo || isFreeCode) && (
                                                                    <span className="text-[9px] font-mono uppercase tracking-wider bg-emerald-500/15 text-emerald-300 px-1.5 py-0.5 rounded">
                                                                        {isFreeCode ? "Promo · Free" : (m.is_free_promo ? m.promo_reason || "Free promo" : "Free")}
                                                                    </span>
                                                                )}
                                                            </div>
                                                            {m.description && (
                                                                <div className="text-[10px] font-mono uppercase tracking-wider text-[var(--text-muted)] mt-0.5 break-words">
                                                                    {m.description}
                                                                </div>
                                                            )}
                                                        </div>
                                                        <div className="text-sm font-mono text-white shrink-0">
                                                            {isFreeCode || cost === 0 ? "Free" : `A$${cost.toFixed(2)}`}
                                                        </div>
                                                        {active && (
                                                            <span className="w-2 h-2 rounded-full bg-[var(--gold)] shrink-0" />
                                                        )}
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    </div>
                                )}

                                {/* Method selector */}
                                <div className="mt-5">
                                    <p className="text-[10px] font-mono uppercase tracking-[0.25em] text-[var(--text-muted)] mb-2">
                                        Payment method
                                    </p>
                                    <div className="space-y-2">
                                        {METHODS.map((m) => {
                                            const Icon = m.icon;
                                            const active = method === m.id;
                                            return (
                                                <button
                                                    key={m.id}
                                                    onClick={() => setMethod(m.id)}
                                                    type="button"
                                                    className={`w-full flex items-center gap-3 p-3 rounded-xl border transition-all text-left ${
                                                        active
                                                            ? "border-[var(--gold)] bg-[rgba(233,188,11,0.08)]"
                                                            : "border-white/10 hover:border-white/20 bg-black/20"
                                                    }`}
                                                    data-testid={`payment-method-${m.id}`}
                                                >
                                                    <Icon
                                                        size={16}
                                                        className={active ? "text-[var(--gold)]" : "text-[var(--text-muted)]"}
                                                    />
                                                    <div className="flex-1">
                                                        <div className="text-sm text-white font-semibold">{m.label}</div>
                                                        <div className="text-[10px] font-mono uppercase tracking-wider text-[var(--text-muted)]">
                                                            {m.sub}
                                                        </div>
                                                    </div>
                                                    {active && (
                                                        <span className="w-2 h-2 rounded-full bg-[var(--gold)]" />
                                                    )}
                                                </button>
                                            );
                                        })}
                                    </div>
                                </div>

                                {/* Out-of-stock warning */}
                                {hasOosBlocker && (
                                    <div className="mt-5 p-4 rounded-2xl border border-red-500/40 bg-red-500/5" data-testid="cart-oos-warning">
                                        <p className="text-xs font-mono uppercase tracking-widest text-red-300 mb-2 flex items-center gap-1.5">
                                            <XCircle size={12} /> Unavailable items in your cart
                                        </p>
                                        <ul className="text-sm text-white space-y-1">
                                            {oosItems.map((it) => (
                                                <li key={it.product_slug} className="flex items-center justify-between gap-3" data-testid={`oos-item-${it.product_slug}`}>
                                                    <span className="truncate">{it.name || it.product_slug}</span>
                                                    <span className="text-[10px] font-mono uppercase text-red-300 shrink-0">
                                                        {it.reason === "insufficient_stock"
                                                            ? `Only ${it.available} left`
                                                            : "Out of stock"}
                                                    </span>
                                                </li>
                                            ))}
                                        </ul>
                                        <p className="text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)] mt-3">
                                            Remove these items to continue to checkout.
                                        </p>
                                    </div>
                                )}

                                {/* Action button — only manual methods (Bank Transfer / PayID)
                                 *  are available post the 2026-02-20 lockdown. Stripe-powered
                                 *  rails (Card, BNPL, Apple/Google Pay, Afterpay, Zip, Temble)
                                 *  are all disabled server-side AND removed from the UI. */}
                                <div className="mt-5">
                                    {method && METHODS.find((m) => m.id === method)?.manual && (
                                        <button
                                            onClick={manualCheckout}
                                            disabled={busy || hasOosBlocker}
                                            className={`btn-solid w-full ${busy || hasOosBlocker ? "opacity-50 cursor-not-allowed" : ""}`}
                                            data-testid="manual-checkout-button"
                                        >
                                            {busy
                                                ? "Reserving order…"
                                                : hasOosBlocker
                                                    ? "Remove out-of-stock items"
                                                    : (
                                                        <>
                                                            Get {METHODS.find((m) => m.id === method)?.label} Instructions{" "}
                                                            <ArrowRight size={14} />
                                                        </>
                                                )}
                                        </button>
                                    )}
                                    {!method && manualMethods.length === 0 && (
                                        <div
                                            className="text-xs text-amber-300 bg-amber-500/10 border border-amber-500/30 rounded-xl px-3 py-2 text-center"
                                            data-testid="no-methods-notice"
                                        >
                                            No payment methods are currently available. Please contact
                                            support@carbonlabsau.com.
                                        </div>
                                    )}
                                </div>

                                <div className="mt-4 flex items-center gap-2 justify-center text-xs text-[var(--text-muted)]">
                                    <ShieldCheck size={12} className="text-[var(--neon-cyan)]" /> 256-bit SSL · Secure checkout
                                </div>
                                <div className="mt-3 flex gap-2 justify-center opacity-60 text-[10px] font-mono uppercase tracking-wider">
                                    {manualMethods.map((m) => m.name).join(" · ")}
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

const FreeShippingProgress = ({ promo }) => {
    if (!promo) return null;
    const { method_name, threshold, remaining, unlocked, progressPct } = promo;
    return (
        <div
            className={`mb-4 p-3 rounded-xl border ${
                unlocked
                    ? "border-emerald-500/40 bg-emerald-500/5"
                    : "border-[var(--gold)]/30 bg-[rgba(233,188,11,0.05)]"
            }`}
            data-testid="free-shipping-progress"
        >
            <div className="flex items-center justify-between gap-3 mb-2">
                <div className="flex items-center gap-2">
                    <Truck size={14} className={unlocked ? "text-emerald-300" : "text-[var(--gold)]"} />
                    <span className="text-xs text-white font-semibold leading-tight">
                        {unlocked ? (
                            <>Free <span className="text-emerald-300">{method_name}</span> unlocked</>
                        ) : (
                            <>
                                Spend{" "}
                                <span className="text-[var(--gold)] font-mono font-bold" data-testid="free-shipping-remaining">
                                    A${remaining.toFixed(2)}
                                </span>{" "}
                                more for free <span className="text-[var(--gold)]">{method_name}</span>
                            </>
                        )}
                    </span>
                </div>
                <span className="text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)] whitespace-nowrap">
                    A${threshold.toFixed(0)}+
                </span>
            </div>
            <div className="h-1.5 bg-white/5 rounded-full overflow-hidden">
                <div
                    className={`h-full rounded-full transition-all duration-500 ${
                        unlocked
                            ? "bg-gradient-to-r from-emerald-400 to-emerald-300"
                            : "bg-gradient-to-r from-[var(--gold)] to-amber-200"
                    }`}
                    style={{ width: `${progressPct}%` }}
                    data-testid="free-shipping-progress-bar"
                />
            </div>
        </div>
    );
};

